/**
 * Build the report header (logo + 4 text lines + study-info grid) as SFDT blocks
 * and install them at the top of the document. Uses `editor.open()` with a
 * fully-composed SFDT instead of paste/insertText/insertTable — the open() path
 * is Syncfusion's most reliable and matches exactly what their own reader handles.
 *
 * Public API:
 *   installHeader(container, cfg, study)        — new/blank report (replaces doc)
 *   installHeaderPrependingBody(container, ...) — template loaded, prepend header
 */
import type { DocumentEditorContainerComponent } from '@syncfusion/ej2-react-documenteditor';
import {
  HeaderConfig, migrateHeaderConfig, StudyFields, TextElement, FONT_FAMILIES,
  cellToString,
} from '../components/HeaderView';

type Container = DocumentEditorContainerComponent;

function extractFontName(cssFont?: string): string {
  if (!cssFont) return 'Arial';
  return cssFont.split(',')[0].trim().replace(/^["']|["']$/g, '');
}

function weightBool(w?: TextElement['weight']): boolean {
  return w === 'bold' || w === 'black';
}

// Cell content flattening lives in HeaderView.cellToString - it handles both
// the new segment shape (mixed text + placeholders) and the legacy single-field
// shape, so this file just concatenates the resolved string for the SFDT run.

/** Build an SFDT paragraph for one of the 4 named text elements, centered. */
function textParagraph(el: TextElement | undefined): any | null {
  if (!el || !el.text || el.visible === false) return null;
  const cf = {
    fontSize: el.sizePt ?? 12,
    bold: weightBool(el.weight),
    fontColor: el.color || '#000000',
    fontFamily: extractFontName(el.fontFamily || FONT_FAMILIES[0].value),
  };
  return {
    paragraphFormat: { textAlignment: 'Center' },
    characterFormat: cf,
    inlines: [{ text: el.text, characterFormat: cf }],
  };
}

/** Build an SFDT paragraph containing the logo as an inline image (centered). */
function logoParagraph(cfg: HeaderConfig): any | null {
  const logo = cfg.logo;
  if (!logo?.dataUri || logo.visible === false) return null;
  // Inline image: store the data URI directly on the inline. Syncfusion's reader
  // accepts imageString both at document root (indexed) and on the inline itself.
  return {
    paragraphFormat: { textAlignment: 'Center' },
    characterFormat: {},
    inlines: [{
      characterFormat: {},
      imageString: logo.dataUri,
      width: logo.maxWidth || 150,
      height: logo.maxHeight || 50,
      isInlineImage: true,
      isMetaFile: false,
    }],
  };
}

/** Build an SFDT table block for the study-info grid. */
function gridTable(cfg: HeaderConfig, study: StudyFields): any | null {
  if (!cfg.grid || cfg.grid.length === 0) return null;

  const numCols = Math.max(1, ...cfg.grid.map((r) => r.cells.length));
  const columnWidthPts = Math.round(468 / numCols);  // ~6.5" / numCols in points
  const gridWidths = Array.from({ length: numCols }, () => columnWidthPts);
  const borderStyle = { lineStyle: 'Single', lineWidth: 0.5, color: '#DBE2EA' };

  return {
    tableFormat: {
      borders: {
        left: borderStyle, right: borderStyle, top: borderStyle, bottom: borderStyle,
        horizontal: borderStyle, vertical: borderStyle,
      },
      preferredWidthType: 'Percent',
      preferredWidth: 100,
    },
    grid: gridWidths,
    columnCount: numCols,
    rows: cfg.grid.map((row) => ({
      rowFormat: {},
      cells: Array.from({ length: numCols }, (_, ci) => {
        const cellCfg = row.cells[ci];
        const content = cellCfg ? cellToString(cellCfg, study) : '';
        // Per-cell font/size/weight/color from HeaderGridCell. Fall back
        // to the 10pt-Arial-normal-black defaults if unset so legacy
        // configs render exactly as before.
        const cf: any = {
          fontSize: cellCfg?.sizePt ?? 10,
          fontFamily: extractFontName(cellCfg?.fontFamily) || 'Arial',
          bold: weightBool(cellCfg?.weight),
        };
        if (cellCfg?.color) cf.fontColor = cellCfg.color;
        return {
          columnIndex: ci,
          cellFormat: {
            preferredWidthType: 'Point',
            preferredWidth: columnWidthPts,
            cellWidth: columnWidthPts,
            borders: {
              left: borderStyle, right: borderStyle, top: borderStyle, bottom: borderStyle,
            },
          },
          blocks: [{
            paragraphFormat: {},
            characterFormat: cf,
            inlines: content ? [{ text: content, characterFormat: cf }] : [],
          }],
        };
      }),
    })),
  };
}

/** Trailing blank paragraph so the user can type below the header grid. */
/** Style name we stamp on the trailing-blank paragraph of the header. Later,
 *  when re-opening a saved report, we walk the document from the top and
 *  drop every block up to AND INCLUDING the paragraph carrying this sentinel.
 *  That gets us the "body" (whatever the user typed after the header) which
 *  we then re-glue under a freshly-generated header. This is how the report
 *  auto-picks-up header-designer changes across saves. */
export const HEADER_END_SENTINEL = 'EdgeHeaderEnd';

/**
 * Heuristic detector: does this block list start with what looks like our
 * report header? Used when loading a template body to avoid stacking a
 * second header on top of one that got baked into the template at save
 * time (which happens with legacy templates because the styleName sentinel
 * strip doesn't survive Syncfusion round-trips).
 *
 * Signature we look for: a Table (which is always our patient-info grid)
 * within the first N blocks. Tables inside body paragraphs are extremely
 * rare in radiology templates, so this false-positive rate is low.
 * Returns the index one past the last header block (i.e., where the body
 * starts), or -1 when no header is detected.
 */
export function detectBakedHeaderEndIndex(blocks: any[]): number {
  const MAX_HEADER_LOOKAHEAD = 8;
  let tableIndex = -1;
  const scanLimit = Math.min(blocks.length, MAX_HEADER_LOOKAHEAD);
  for (let i = 0; i < scanLimit; i++) {
    const b = blocks[i];
    // Table blocks have `rows: []`; paragraph blocks have `inlines: []`.
    if (b?.rows && Array.isArray(b.rows) && b.rows.length > 0) {
      tableIndex = i;
      break;
    }
  }
  if (tableIndex < 0) return -1;
  // The header ends at the table + one trailing empty paragraph (that's
  // our sentinel-blank). Some legacy saves drop the trailing blank so we
  // check whether the next block IS an empty paragraph before consuming it.
  const nextIdx = tableIndex + 1;
  if (nextIdx < blocks.length) {
    const next = blocks[nextIdx];
    const isTrailingBlank =
      next && next.inlines && Array.isArray(next.inlines) &&
      (next.inlines.length === 0 || (next.inlines.length === 1 && !next.inlines[0].text));
    if (isTrailingBlank) return nextIdx + 1;
  }
  return tableIndex + 1;
}

/** Trailing blank paragraph immediately after the header. Marked with a
 *  distinctive styleName so we can locate it later in the saved SFDT. */
const TRAILING_PARAGRAPH = {
  paragraphFormat: { styleName: HEADER_END_SENTINEL },
  characterFormat: {},
  inlines: [],
};

/** Build the ordered list of SFDT blocks that make up the header. */
export function buildHeaderBlocks(cfg: HeaderConfig, study: StudyFields): any[] {
  const blocks: any[] = [];
  const logo = logoParagraph(cfg);
  if (logo) blocks.push(logo);
  const els = cfg.elements || {};
  const keys: ('hospitalName' | 'address' | 'department' | 'title')[] =
    ['hospitalName', 'address', 'department', 'title'];
  for (const k of keys) {
    const p = textParagraph(els[k]);
    if (p) blocks.push(p);
  }
  const table = gridTable(cfg, study);
  if (table) blocks.push(table);
  blocks.push(TRAILING_PARAGRAPH);
  return blocks;
}

/** Fallback page geometry (US Letter, 1" margins). All builders below
 *  accept an OPTIONAL PageFormat param; when omitted the fallback wins so
 *  old callers keep working. Note: import type inline to avoid a cross-file
 *  cycle between insertHeader.ts and pageFormat.ts. */
interface PageFormatLike {
  // Paper
  pageWidth: number;
  pageHeight: number;
  // Margins
  leftMargin: number;
  rightMargin: number;
  topMargin: number;
  bottomMargin: number;
  // Layout (all optional so hand-built partials still work)
  headerDistance?: number;
  footerDistance?: number;
  differentFirstPage?: boolean;
  differentOddAndEvenPages?: boolean;
  numberOfColumns?: number;
  equalWidth?: boolean;
  lineBetweenColumns?: boolean;
}

const FALLBACK_PAGE_FORMAT: PageFormatLike = {
  pageWidth: 612, pageHeight: 792,
  leftMargin: 72, rightMargin: 72, topMargin: 72, bottomMargin: 72,
  headerDistance: 36, footerDistance: 36,
  differentFirstPage: false, differentOddAndEvenPages: false,
  numberOfColumns: 1, equalWidth: true, lineBetweenColumns: false,
};

/** Turn a PageFormatLike into the SFDT sectionFormat object shape. Only
 *  emits keys that are actually set on the input - keeps the SFDT lean
 *  and lets Syncfusion's parser apply its own defaults for anything
 *  the user hasn't customized. Matches the field names in SectionFormat-
 *  Properties (see the Page Setup dialog source at page-setup-dialog.js). */
function toSectionFormat(pf: PageFormatLike | undefined): any {
  const p = pf || FALLBACK_PAGE_FORMAT;
  const out: any = {
    pageWidth: p.pageWidth,
    pageHeight: p.pageHeight,
    leftMargin: p.leftMargin,
    rightMargin: p.rightMargin,
    topMargin: p.topMargin,
    bottomMargin: p.bottomMargin,
  };
  if (p.headerDistance          !== undefined) out.headerDistance          = p.headerDistance;
  if (p.footerDistance          !== undefined) out.footerDistance          = p.footerDistance;
  if (p.differentFirstPage      !== undefined) out.differentFirstPage      = p.differentFirstPage;
  if (p.differentOddAndEvenPages !== undefined) out.differentOddAndEvenPages = p.differentOddAndEvenPages;
  if (p.numberOfColumns         !== undefined) out.numberOfColumns         = p.numberOfColumns;
  if (p.equalWidth              !== undefined) out.equalWidth              = p.equalWidth;
  if (p.lineBetweenColumns      !== undefined) out.lineBetweenColumns      = p.lineBetweenColumns;
  return out;
}

/** Build a fresh, known-good SFDT document with [header + arbitrary body
 *  blocks]. Uses the SAME top-level structure (sectionFormat, characterFormat,
 *  defaultTabWidth) that installHeader uses for brand-new reports - which is
 *  a proven-to-render shape. When we tried to merge into a template's
 *  existing document structure the header sometimes wouldn't render because
 *  the template SFDT was missing fields Syncfusion needs. This helper
 *  discards the template's top-level structure and rebuilds it. */
export function buildCombinedDocument(
  rawConfig: HeaderConfig | null,
  study: StudyFields,
  bodyBlocks: any[],
  pageFormat?: PageFormatLike,
): any {
  const cfg = migrateHeaderConfig(rawConfig);
  return {
    sections: [{
      sectionFormat: toSectionFormat(pageFormat),
      blocks: [...buildHeaderBlocks(cfg, study), ...bodyBlocks],
    }],
    characterFormat: { fontSize: 11, fontFamily: 'Calibri' },
    paragraphFormat: {},
    defaultTabWidth: 36,
  };
}

/** Wrap header blocks in a full SFDT document. */
function buildHeaderDocument(
  cfg: HeaderConfig, study: StudyFields, pageFormat?: PageFormatLike,
): any {
  return {
    sections: [{
      sectionFormat: toSectionFormat(pageFormat),
      blocks: buildHeaderBlocks(cfg, study),
    }],
    characterFormat: { fontSize: 11, fontFamily: 'Calibri' },
    paragraphFormat: {},
    defaultTabWidth: 36,
  };
}

/** Build the header as a full SFDT string, ready for editor.editor.paste(). */
export function buildHeaderSfdtString(
  rawConfig: HeaderConfig | null,
  study: StudyFields,
  pageFormat?: PageFormatLike,
): string {
  const cfg = migrateHeaderConfig(rawConfig);
  return JSON.stringify(buildHeaderDocument(cfg, study, pageFormat));
}

/**
 * Move the caret to document start and paste the header there using
 * Syncfusion's public paste API. This is the "iron-clad" way to combine
 * a body document with a header - it uses the same code path as clipboard
 * paste, which handles style dedup, list-id remap, and image merging.
 *
 * Call this AFTER `await editor.openAsync(bodySfdt)` returns.
 */
export function pasteHeaderAtDocumentStart(
  container: Container | null,
  rawConfig: HeaderConfig | null,
  study: StudyFields,
  pageFormat?: PageFormatLike,
): void {
  if (!container) return;
  const editor: any = container.documentEditor;
  if (!editor?.editor || !editor.selection) return;
  const headerSfdt = buildHeaderSfdtString(rawConfig, study, pageFormat);
  try {
    editor.selection.moveToDocumentStart();
    editor.editor.paste(headerSfdt, 'KeepSourceFormatting');
  } catch (err) {
    console.error('pasteHeaderAtDocumentStart: paste threw', err);
  }
}

/**
 * Replace the editor's document with a fresh header-only SFDT.
 * Use for new reports where the editor is blank.
 */
export function installHeader(
  container: Container | null,
  rawConfig: HeaderConfig | null,
  study: StudyFields,
  pageFormat?: PageFormatLike,
): void {
  if (!container) return;
  const cfg = migrateHeaderConfig(rawConfig);
  const editor: any = container.documentEditor;
  if (!editor) return;

  try {
    editor.open(JSON.stringify(buildHeaderDocument(cfg, study, pageFormat)));
  } catch (err) {
    console.error('installHeader: editor.open threw', err);
  }
}

/**
 * Prepend the header blocks to whatever content is currently loaded in the
 * editor (e.g., a template body). Merges the two SFDT documents at the
 * sections[0].blocks level and calls editor.open() with the combined document.
 */
export function installHeaderPrependingBody(
  container: Container | null,
  rawConfig: HeaderConfig | null,
  study: StudyFields
): void {
  if (!container) return;
  const cfg = migrateHeaderConfig(rawConfig);
  const editor: any = container.documentEditor;
  if (!editor) return;

  let bodySfdt: any;
  try {
    bodySfdt = JSON.parse(editor.serialize());
  } catch (err) {
    console.error('installHeaderPrependingBody: serialize failed', err);
    installHeader(container, rawConfig, study);
    return;
  }

  const headerBlocks = buildHeaderBlocks(cfg, study);
  const existingBlocks = (bodySfdt?.sections?.[0]?.blocks) || [];
  if (!bodySfdt.sections) bodySfdt.sections = [{ sectionFormat: {}, blocks: [] }];
  if (!bodySfdt.sections[0]) bodySfdt.sections[0] = { sectionFormat: {}, blocks: [] };
  bodySfdt.sections[0].blocks = [...headerBlocks, ...existingBlocks];

  try {
    editor.open(JSON.stringify(bodySfdt));
  } catch (err) {
    console.error('installHeaderPrependingBody: open threw', err);
  }
}

/**
 * Walk from the top of a parsed SFDT document and drop every block up to
 * and including the paragraph tagged with HEADER_END_SENTINEL. Returns the
 * new blocks array (the "body"). If the sentinel isn't found, returns the
 * input unchanged - old saves from before the sentinel was added still
 * render, they just don't get header-refresh (upgrade path: user saves once
 * on the new build, sentinel lands, subsequent loads refresh normally).
 */
function stripHeaderBlocks(blocks: any[]): { body: any[]; hadHeader: boolean } {
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (b?.paragraphFormat?.styleName === HEADER_END_SENTINEL) {
      return { body: blocks.slice(i + 1), hadHeader: true };
    }
  }
  return { body: blocks, hadHeader: false };
}

/**
 * Refresh the header from the current HeaderConfig on top of a saved
 * report. Called after loading an existing draft or signed report so
 * changes made in the Header Designer since the last save show up when
 * the report is reopened.
 *
 * Flow:
 *   1. serialize() the currently-loaded doc
 *   2. drop the old header from sections[0].blocks (up to sentinel)
 *   3. prepend a freshly-generated header
 *   4. editor.open() the recombined doc
 *
 * No-ops silently if the loaded doc has no header sentinel (pre-sentinel
 * save) - we leave the old baked-in header in place rather than risk
 * duplicating it. The user's next save writes a sentinel-tagged header
 * and future loads refresh normally.
 */
export function refreshHeaderFromSavedDoc(
  container: Container | null,
  rawConfig: HeaderConfig | null,
  study: StudyFields
): void {
  if (!container) return;
  const editor: any = container.documentEditor;
  if (!editor) return;

  let doc: any;
  try {
    doc = JSON.parse(editor.serialize());
  } catch (err) {
    console.error('refreshHeaderFromSavedDoc: serialize failed', err);
    return;
  }

  const existingBlocks = doc?.sections?.[0]?.blocks || [];
  const { body, hadHeader } = stripHeaderBlocks(existingBlocks);
  if (!hadHeader) {
    // Legacy save - don't touch. Next save will add the sentinel.
    return;
  }

  const cfg = migrateHeaderConfig(rawConfig);
  const headerBlocks = buildHeaderBlocks(cfg, study);

  if (!doc.sections) doc.sections = [{ sectionFormat: {}, blocks: [] }];
  if (!doc.sections[0]) doc.sections[0] = { sectionFormat: {}, blocks: [] };
  doc.sections[0].blocks = [...headerBlocks, ...body];

  try {
    editor.open(JSON.stringify(doc));
  } catch (err) {
    console.error('refreshHeaderFromSavedDoc: open threw', err);
  }
}

/**
 * Strip the header from a serialized SFDT string. Returns a new SFDT string
 * with the header blocks removed (up to and including the paragraph marked
 * with HEADER_END_SENTINEL). If no sentinel is found, returns the input
 * unchanged - safe for content that never had a header.
 *
 * Use case: saving a report as a template. The header has resolved placeholder
 * values ("Patient: Doe^John", "MRN: 00421") baked in as literal text. If we
 * saved the template as-is, every future use of the template would show
 * Doe^John's name regardless of the actual patient. Strip the header so the
 * template body carries only what the user actually typed; the header is
 * regenerated fresh from each caller's HeaderConfig + study.
 */
export function stripHeaderFromSfdt(sfdt: string): string {
  let doc: any;
  try {
    doc = JSON.parse(sfdt);
  } catch {
    return sfdt;
  }
  const blocks = doc?.sections?.[0]?.blocks || [];
  const { body, hadHeader } = stripHeaderBlocks(blocks);
  if (!hadHeader) return sfdt;

  if (!doc.sections) doc.sections = [{ sectionFormat: {}, blocks: [] }];
  if (!doc.sections[0]) doc.sections[0] = { sectionFormat: {}, blocks: [] };
  doc.sections[0].blocks = body;
  return JSON.stringify(doc);
}

/**
 * Belt-and-suspenders: after any content load in report mode, make sure
 * the header is intact at the top. If it's already there (sentinel at
 * the expected index), no-op. If it's missing or the sentinel drifted
 * (e.g. user edited it, or a template load didn't include one), rebuild:
 * strip any partial-header remnants and prepend a fresh header block.
 *
 * Used from applyTemplate as a safety net over the "build combined SFDT
 * in memory" primary path - when that path silently failed to inject
 * the header (headerConfigRef not yet loaded, template structure edge
 * case, etc.) users saw a template body with no header on top. This
 * fires 300 ms after the load and rescues those cases without risking
 * a double-header on the successful path.
 */
export function ensureHeaderPresent(
  container: Container | null,
  rawConfig: HeaderConfig | null,
  study: StudyFields,
): void {
  if (!container) return;
  const editor: any = container.documentEditor;
  if (!editor) return;

  let doc: any;
  try {
    doc = JSON.parse(editor.serialize());
  } catch { return; }

  const cfg = migrateHeaderConfig(rawConfig);
  const expectedHeader = buildHeaderBlocks(cfg, study);
  const currentBlocks = doc?.sections?.[0]?.blocks || [];
  const sentinelIdx = currentBlocks.findIndex(
    (b: any) => b?.paragraphFormat?.styleName === HEADER_END_SENTINEL,
  );
  // Header is intact: sentinel sits at the right spot. Do nothing.
  if (sentinelIdx === expectedHeader.length - 1) return;

  // Header is missing / drifted. Strip whatever remnant is at the top
  // (sentinel found = strip up to it, not found = leave everything as body)
  // then prepend a fresh header.
  const { body } = stripHeaderBlocks(currentBlocks);
  if (!doc.sections) doc.sections = [{ sectionFormat: {}, blocks: [] }];
  if (!doc.sections[0]) doc.sections[0] = { sectionFormat: {}, blocks: [] };
  doc.sections[0].blocks = [...expectedHeader, ...body];

  try {
    editor.open(JSON.stringify(doc));
  } catch (err) {
    console.error('ensureHeaderPresent: editor.open threw', err);
  }
}

// Back-compat: old callers used `insertHeader(...)`. Keep the name but route
// through installHeader (replaces doc).
export const insertHeader = installHeader;
